import React, { useState, useEffect } from "react";
import {
  Button,
  Input,
  CircularProgress,
  Chip,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Checkbox,
} from "@nextui-org/react";
import { BsTrash } from "react-icons/bs";
import { IoCloseSharp } from "react-icons/io5";
import { MdEdit, MdCheck } from "react-icons/md";
import { LuChevronsUpDown, LuSearch, LuPlus } from "react-icons/lu";
import { useTranslation } from "react-i18next";

import myAxios from "../../utils/axiosWrapper";

const ORG_TYPES = [
  { key: "department", label: "Départements", apiKey: "departments" },
  { key: "localisation", label: "Localisations", apiKey: "localisations" },
  { key: "team", label: "Équipes", apiKey: "teams" },
];

const ManagerPicker = ({ managerIds, operatorOptions, getOperatorName, onChange, t }) => {
  const [managerSearch, setManagerSearch] = useState("");
  const selectedSet = new Set(managerIds || []);

  const filtered = operatorOptions.filter((op) => {
    if (!managerSearch.trim()) return true;
    return op.label.toLowerCase().includes(managerSearch.trim().toLowerCase());
  });

  return (
    <Popover
      placement="bottom-start"
      onOpenChange={(open) => {
        if (!open) setManagerSearch("");
      }}
    >
      <PopoverTrigger>
        <button
          type="button"
          className="w-full min-h-[40px] px-3 py-2 rounded-medium border-2 text-left flex items-center justify-between gap-2 border-default-200 bg-default-50 hover:border-default-400 cursor-pointer"
        >
          <div className="flex-1 min-w-0">
            {selectedSet.size === 0 ? (
              <span className="text-sm text-gray-400">{t("Sélectionner les managers")}</span>
            ) : (
              <div className="flex flex-wrap gap-1">
                {[...selectedSet].map((id) => (
                  <Chip
                    key={id}
                    size="sm"
                    variant="flat"
                    color="primary"
                  >
                    {getOperatorName(id)}
                  </Chip>
                ))}
              </div>
            )}
          </div>
          <LuChevronsUpDown
            size={16}
            className="text-gray-500 shrink-0"
          />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[320px]">
        <div className="w-full p-2 border-b border-gray-200">
          <Input
            autoFocus
            placeholder={t("Rechercher un manager...")}
            value={managerSearch}
            onChange={(e) => setManagerSearch(e.target.value)}
            size="sm"
            startContent={
              <LuSearch
                size={14}
                className="text-gray-400"
              />
            }
            classNames={{ input: "text-sm" }}
          />
        </div>
        <div className="max-h-[260px] overflow-y-auto py-1 w-full">
          {filtered.length === 0 && (
            <div className="text-center text-xs text-gray-500 py-4">{t("Aucun résultat")}</div>
          )}
          {filtered.map((op) => {
            const isSelected = selectedSet.has(op.key);
            return (
              <button
                type="button"
                key={op.key}
                onClick={() => {
                  const next = new Set(selectedSet);
                  if (isSelected) {
                    next.delete(op.key);
                  } else {
                    next.add(op.key);
                  }
                  onChange(Array.from(next));
                }}
                className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-100 text-left text-sm"
              >
                <Checkbox
                  isSelected={isSelected}
                  size="sm"
                  onValueChange={() => {}}
                  className="pointer-events-none"
                />
                <span className="flex-1 truncate">{op.label}</span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const OrganisationsPanel = ({ apiUrl, onOrganisationsChanged = () => {} }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("department");
  const [organisations, setOrganisations] = useState({
    departments: [],
    localisations: [],
    teams: [],
  });
  const [operators, setOperators] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingState, setEditingState] = useState({ name: "", manager_ids: [] });
  const [newItemName, setNewItemName] = useState("");
  const [savingId, setSavingId] = useState(null);
  const [itemSearch, setItemSearch] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchOrganisations = async () => {
    setIsLoading(true);
    try {
      const orgRes = await myAxios.get(`${apiUrl}/audio/api/v1/admin/company_organisations`);
      const orgData = orgRes?.data?.[0] || {};
      setOrganisations({
        departments: orgData.departments || [],
        localisations: orgData.localisations || [],
        teams: orgData.teams || [],
      });

      const opRes = await myAxios.get(`${apiUrl}/audio/api/v1/admin/simple_operators`);
      const operatorsRaw = opRes?.data?.[0]?.operators;
      setOperators(Array.isArray(operatorsRaw) ? operatorsRaw : []);
    } catch (error) {
      console.error("Error fetching organisations:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganisations();
  }, []);

  const currentOrgType = ORG_TYPES.find((type) => type.key === activeTab);
  const currentItems = organisations[currentOrgType?.apiKey] || [];
  const filteredItems = currentItems
    .filter((item) => {
      if (!itemSearch.trim()) return true;
      return (item.name || "").toLowerCase().includes(itemSearch.trim().toLowerCase());
    })
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  const operatorOptions = operators
    .filter((op) => op?.is_active !== false)
    .map((op) => ({
      key: op.id,
      label: `${op.first_name || ""} ${op.last_name || ""}`.trim() || op.email,
    }));

  const getOperatorName = (operatorId) => {
    const op = operators.find((o) => o.id === operatorId);
    if (!op) return operatorId;
    return `${op.first_name || ""} ${op.last_name || ""}`.trim() || op.email;
  };

  const handleStartEdit = (item) => {
    setEditingId(item.id);
    setEditingState({
      name: item.name || "",
      manager_ids: item.manager_ids || [],
    });
  };

  const handleSaveEdit = async (item) => {
    setSavingId(item.id);
    try {
      await myAxios.put(`${apiUrl}/audio/api/v1/admin/update_org_item/${activeTab}/${item.id}`, {
        name: editingState.name,
        manager_ids: editingState.manager_ids,
      });
      setEditingId(null);
      await fetchOrganisations();
      onOrganisationsChanged();
    } catch (error) {
      console.error("Error updating org item:", error);
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async (item) => {
    setSavingId(item.id);
    try {
      await myAxios.delete(`${apiUrl}/audio/api/v1/admin/delete_org_item/${activeTab}/${item.id}`);
      setDeleteTarget(null);
      await fetchOrganisations();
      onOrganisationsChanged();
    } catch (error) {
      console.error("Error deleting org item:", error);
    } finally {
      setSavingId(null);
    }
  };

  const handleCreate = async () => {
    if (!newItemName.trim()) return;
    setIsLoading(true);
    try {
      await myAxios.post(`${apiUrl}/audio/api/v1/admin/create_org_item`, {
        type: activeTab,
        name: newItemName.trim(),
      });
      setNewItemName("");
      await fetchOrganisations();
      onOrganisationsChanged();
    } catch (error) {
      console.error("Error creating org item:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl p-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-1 bg-[#F5F5F5] rounded-lg p-1">
          {ORG_TYPES.map((type) => {
            const count = (organisations[type.apiKey] || []).length;
            const isActive = activeTab === type.key;
            return (
              <button
                key={type.key}
                type="button"
                onClick={() => {
                  setActiveTab(type.key);
                  setEditingId(null);
                  setNewItemName("");
                  setItemSearch("");
                }}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-white text-primary shadow-sm"
                    : "text-[#737373] hover:text-[#404040]"
                }`}
              >
                {t(type.label)}
                <span
                  className={`ml-2 text-xs px-1.5 py-0.5 rounded-full ${
                    isActive ? "bg-primary/10 text-primary" : "bg-[#E5E5E5] text-[#737373]"
                  }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Input
            placeholder={t("Rechercher...")}
            value={itemSearch}
            onChange={(e) => setItemSearch(e.target.value)}
            isClearable
            onClear={() => setItemSearch("")}
            size="sm"
            className="w-[220px]"
            startContent={
              <LuSearch
                size={16}
                className="text-gray-400"
              />
            }
            classNames={{ input: "text-sm" }}
          />
          <Input
            placeholder={t("Name")}
            value={newItemName}
            onChange={(e) => setNewItemName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleCreate();
              }
            }}
            size="sm"
            className="w-[200px]"
            classNames={{ input: "text-sm" }}
          />
          <Button
            color="primary"
            size="sm"
            onClick={handleCreate}
            isDisabled={!newItemName.trim()}
            startContent={<LuPlus size={16} />}
          >
            {t("Créer")}
          </Button>
        </div>
      </div>

      <div className="mt-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <CircularProgress />
          </div>
        ) : (
          <>
            {currentItems.length === 0 && (
              <div className="text-center text-sm text-gray-500 py-12">{t("Aucun élément")}</div>
            )}
            {currentItems.length > 0 && filteredItems.length === 0 && (
              <div className="text-center text-sm text-gray-500 py-12">{t("Aucun résultat")}</div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {filteredItems.map((item) => {
                const isEditing = editingId === item.id;
                const isSaving = savingId === item.id;
                const managerIds = isEditing ? editingState.manager_ids : item.manager_ids || [];

                return (
                  <div
                    key={item.id}
                    className={`border rounded-xl p-4 transition-colors ${
                      isEditing
                        ? "border-primary/40 bg-primary/[0.03]"
                        : "border-[#E5E5E5] bg-white"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        {isEditing ? (
                          <Input
                            value={editingState.name}
                            onChange={(e) =>
                              setEditingState({ ...editingState, name: e.target.value })
                            }
                            size="sm"
                            variant="bordered"
                            classNames={{ input: "text-sm font-medium" }}
                          />
                        ) : (
                          <div
                            className="text-sm font-medium text-[#171717] truncate"
                            title={item.name}
                          >
                            {item.name}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {isEditing ? (
                          <>
                            <button
                              type="button"
                              onClick={() => handleSaveEdit(item)}
                              disabled={isSaving}
                              title={t("Save")}
                              className="p-2 bg-green-100 hover:bg-green-200 rounded-lg transition-colors disabled:opacity-50"
                            >
                              {isSaving ? (
                                <CircularProgress size="sm" />
                              ) : (
                                <MdCheck
                                  size={16}
                                  className="text-green-600"
                                />
                              )}
                            </button>
                            <button
                              type="button"
                              onClick={() => setEditingId(null)}
                              title={t("Cancel")}
                              className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                            >
                              <IoCloseSharp
                                size={16}
                                className="text-gray-600"
                              />
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              type="button"
                              onClick={() => handleStartEdit(item)}
                              title={t("Edit")}
                              className="p-2 hover:bg-blue-50 rounded-lg transition-colors"
                            >
                              <MdEdit
                                size={16}
                                className="text-blue-600"
                              />
                            </button>
                            <button
                              type="button"
                              onClick={() => setDeleteTarget(item)}
                              disabled={isSaving}
                              title={t("Supprimer")}
                              className="p-2 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                            >
                              <BsTrash
                                size={14}
                                className="text-red-500"
                              />
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="mt-3">
                      <div className="text-xs font-medium text-gray-500 mb-1">{t("Managers")}</div>
                      {isEditing ? (
                        <ManagerPicker
                          managerIds={managerIds}
                          operatorOptions={operatorOptions}
                          getOperatorName={getOperatorName}
                          onChange={(nextIds) =>
                            setEditingState({ ...editingState, manager_ids: nextIds })
                          }
                          t={t}
                        />
                      ) : managerIds.length === 0 ? (
                        <span className="text-xs text-gray-400">{t("Aucun manager")}</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {managerIds.map((id) => (
                            <Chip
                              key={id}
                              size="sm"
                              variant="flat"
                              color="primary"
                            >
                              {getOperatorName(id)}
                            </Chip>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {deleteTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-2xl shadow-xl w-[90vw] max-w-[440px] p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {t("Confirmer la suppression")}
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              {t("Voulez-vous vraiment supprimer")}{" "}
              <span className="font-medium">{deleteTarget.name}</span> ?
            </p>
            <div className="flex justify-end gap-2">
              <Button
                color="default"
                variant="flat"
                onClick={() => setDeleteTarget(null)}
                isDisabled={savingId === deleteTarget.id}
              >
                {t("Annuler")}
              </Button>
              <Button
                color="danger"
                onClick={() => handleDelete(deleteTarget)}
                isLoading={savingId === deleteTarget.id}
              >
                {t("Supprimer")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganisationsPanel;
